import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, TrendingUp } from "lucide-react";

const TIERS = [
  {
    key: "startup",
    label: "Startup",
    range: "< $120K / mo",
    rangeMin: 0,
    rangeMax: 120000,
    priceLabel: "$1,200",
    priceSub: "/mo flat",
    description: "Predictable AI cost for smaller practices — no percentage of collections.",
  },
  {
    key: "growth",
    label: "Growth",
    range: "$120K – $200K / mo",
    rangeMin: 120000,
    rangeMax: 200000,
    priceLabel: "2%",
    priceSub: "of collections",
    description: "As you grow, AI cost scales with you — just a small share.",
  },
  {
    key: "enterprise",
    label: "Enterprise",
    range: "$200K+ / mo",
    rangeMin: 200000,
    rangeMax: Infinity,
    priceLabel: "1.65%",
    priceSub: "of collections",
    description: "Volume discount — the largest practices get the lowest rate.",
  },
];

// Software bundle — PMS + all the other software products that come with
// Asha PMS. Mirrors the CI software list but adds PMS Core + imaging since
// the Asha PMS replaces the customer's existing PMS and imaging system.
const pmsCore = [
  "Smart Scheduling",
  "Clinical Charting",
  "Clinical Notes",
  "Document Management",
  "Digital Imaging",
];

const communicationSuite = [
  "VoIP Phone System",
  "2-Way Messaging",
  "Intelligent Fax",
  "Next-Gen Forms",
  "Team Chat",
  "Online Scheduling",
  "Physical Phones",
];

const marketingSuite = [
  "SMS Campaigns",
  "Email Campaigns",
  "Patient Recalls",
];

const revenueIntelligenceSuite = [
  "Revenue Analytics Dashboard",
  "Daily Huddles Dashboard",
  "Collections Reporting",
  "Production Tracking",
  "Provider Performance",
  "AR Aging Reports",
];

const integratedPaymentsSuite = [
  "Card Readers",
  "Text-to-Pay",
  "Payment Processing",
  "Patient Payment Plans",
];

const claimsPortal = [
  "Unified Claim Submission",
  "Real-time Claim Status",
  "Batch Claim Processing",
];

const insuranceVerification = [
  "Real-time Benefits Check",
  "Comprehensive Breakdowns",
  "Eligibility Monitoring",
];

// AI workforce — same scope as the CI deck since AI agents are identical
// across both engagement models.
const frontOfficeAgents = [
  "Amy — Receptionist",
  "Leena — Schedule Optimizer",
  "Stephanie — Hygiene Recall",
  "Kim — Case Acceptance",
  "Elise — Patient Experience",
  "Morgan — Patient Intake",
  "Trisha — Practice Manager",
  "Joseph — Fax Manager",
];

const backOfficeAgents = [
  "Amanda — Insurance Verification",
  "Ariel — Claim Submission",
  "Alayna — Claim Scrubbing",
  "Sarah — Denial Management",
  "Alisha — EOB Posting",
  "Kianna — Patient Billing",
  "Julia — AR & Collections",
  "Laura — Insurance Calls",
];

const clinicalAndIntelligenceAI = [
  "Asha Copilot",
  "AI Scribe",
  "Voice Perio",
  "AI Treatment Planner",
  "AI Revenue Analytics",
];

const formatCurrency = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const FeatureGroup = ({ title, items, accent, size = "sm", cols = 2 }) => {
  const titleCls =
    size === "md"
      ? "text-[13px] font-bold text-slate-500 uppercase tracking-wider mb-2"
      : "text-[11.5px] font-bold text-slate-500 uppercase tracking-wider mb-1.5";
  const iconCls = size === "md" ? "w-4 h-4" : "w-3 h-3";
  const itemCls =
    size === "md"
      ? "text-[14.5px] text-slate-700 leading-snug font-medium"
      : "text-[12.5px] text-slate-700 leading-snug";
  const rowGap = size === "md" ? "gap-y-2" : "gap-y-1";
  const iconMt = size === "md" ? "mt-[4px]" : "mt-[3px]";
  const gridCls = cols === 1 ? "grid-cols-1" : "grid-cols-2";

  return (
    <div>
      <p className={titleCls}>{title}</p>
      <div className={`grid ${gridCls} gap-x-3 ${rowGap}`}>
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-1.5">
            <Check className={`${iconCls} ${accent} shrink-0 ${iconMt}`} />
            <span className={itemCls}>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AshaPMSPricingSlide = () => {
  const [collections, setCollections] = useState(150000);

  const activeTier = useMemo(() => {
    return (
      TIERS.find((t) => collections >= t.rangeMin && collections < t.rangeMax) ||
      TIERS[TIERS.length - 1]
    );
  }, [collections]);

  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-4 xl:p-6 relative overflow-hidden font-sans">
      {/* Ambient blue gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 50%, #fbcfe8 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1240px]">
        {/* Header — compact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-2.5"
        >
          <h2 className="text-2xl lg:text-[32px] font-bold text-slate-900 tracking-tight leading-tight">
            The only PMS{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              built for AI from the ground up.
            </span>
          </h2>
        </motion.div>

        {/* Collections selector — single inline row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="w-full max-w-[640px] mx-auto mb-3 flex items-center gap-3"
        >
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <TrendingUp className="w-3 h-3 text-blue-600" />
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
              Monthly collections
            </p>
          </div>
          <input
            type="range"
            min={40000}
            max={500000}
            step={5000}
            value={collections}
            onChange={(e) => setCollections(Number(e.target.value))}
            className="flex-1 accent-blue-600 cursor-pointer"
          />
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-sm font-extrabold text-slate-900 tracking-tight tabular-nums">
              {formatCurrency(collections)}
            </span>
            <span className="text-xs text-slate-400">·</span>
            <span className="text-[11px] font-bold uppercase tracking-widest bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent whitespace-nowrap">
              {activeTier.label}
            </span>
          </div>
        </motion.div>

        {/* Two-section pricing card — Software / AI */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="w-full rounded-[20px] overflow-hidden shadow-[0_8px_60px_-12px_rgba(0,0,0,0.08)] border border-white/80 bg-white/95"
        >
          {/* Accent line */}
          <div
            className="h-[3px]"
            style={{
              background: "linear-gradient(90deg, #3b82f6 0%, #6366f1 50%, #3b82f6 100%)",
            }}
          />

          <div className="grid grid-cols-2 relative">
            {/* Bundled pricing indicator — sits over the divider at price row */}
            <div
              aria-hidden
              className="absolute left-1/2 -translate-x-1/2 top-[60px] z-10 w-10 h-10 rounded-full bg-white border border-blue-200 shadow-[0_4px_14px_-3px_rgba(59,130,246,0.3)] flex items-center justify-center"
            >
              <span className="text-xl font-bold text-blue-600 leading-none">+</span>
            </div>

            {/* LEFT — Software / $1,600 */}
            <div className="p-5 border-r border-slate-100 flex flex-col">
              <div className="mb-3 text-center">
                <p className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-1">
                  Software
                </p>
                <div className="flex items-baseline gap-1 justify-center">
                  <span className="text-[38px] font-extrabold tracking-tight text-slate-900 leading-none">
                    $1,600
                  </span>
                  <span className="text-sm font-medium text-slate-400">/mo</span>
                </div>
              </div>

              <div className="space-y-2.5">
                <FeatureGroup
                  title="PMS Core"
                  items={pmsCore}
                  accent="text-blue-500"
                />
                <FeatureGroup
                  title="Communication Suite"
                  items={communicationSuite}
                  accent="text-sky-500"
                />
                <FeatureGroup
                  title="Marketing Suite"
                  items={marketingSuite}
                  accent="text-indigo-500"
                />
                <FeatureGroup
                  title="Revenue Intelligence Suite"
                  items={revenueIntelligenceSuite}
                  accent="text-violet-500"
                />
                <div className="grid grid-cols-3 gap-3">
                  <FeatureGroup
                    title="Payments"
                    items={integratedPaymentsSuite}
                    accent="text-purple-500"
                    cols={1}
                  />
                  <FeatureGroup
                    title="Claims Portal"
                    items={claimsPortal}
                    accent="text-fuchsia-500"
                    cols={1}
                  />
                  <FeatureGroup
                    title="Insurance Verif."
                    items={insuranceVerification}
                    accent="text-emerald-500"
                    cols={1}
                  />
                </div>
              </div>
            </div>

            {/* RIGHT — AI / tiered */}
            <div className="p-5 bg-gradient-to-br from-blue-50/40 via-white to-indigo-50/30 flex flex-col">
              <div className="mb-4 text-center">
                <p className="text-sm font-bold text-indigo-600 uppercase tracking-wider mb-1.5">
                  AI
                </p>
                <motion.div
                  key={activeTier.key}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className="flex items-baseline gap-1 justify-center"
                >
                  <span className="text-[38px] font-extrabold tracking-tight text-slate-900 leading-none">
                    {activeTier.priceLabel}
                  </span>
                  <span className="text-sm font-medium text-slate-400">{activeTier.priceSub}</span>
                </motion.div>
              </div>

              <div className="space-y-3">
                <FeatureGroup
                  title="8 Front Office AI Agents"
                  items={frontOfficeAgents}
                  accent="text-blue-500"
                />
                <FeatureGroup
                  title="8 Back Office AI Agents"
                  items={backOfficeAgents}
                  accent="text-indigo-500"
                />
                <FeatureGroup
                  title="5 Clinical & Intelligence AI"
                  items={clinicalAndIntelligenceAI}
                  accent="text-violet-500"
                />
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-slate-100/80 px-7 py-3 flex items-center justify-center gap-4 bg-slate-50/50">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500" />
              <span className="text-[13.5px] font-medium text-slate-600">
                <strong className="text-slate-700">150,000 AI credits</strong> included &middot;
                overage at <strong className="text-slate-700">2.3¢</strong> per 1,000 AI credits &middot;
                AI runs natively on Asha PMS — no integration fees
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AshaPMSPricingSlide;
